import { useCallback, useState } from 'react'
import {
  Edit,
  Eye,
  FileText,
  MessageSquare,
  IdCard,
  XCircle,
  UserPlus,
  UserMinus,
  UserCog,
  Mail,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { BookingsFilterBar } from './components/BookingsFilterBar'
import { BookingsTableCard } from './components/BookingsTableCard'
import { Breadcrumb } from '../../components/ui/Breadcrumb'
import type { RowAction } from '../../components/ui/RowActionsMenu'
import { shellAssets } from '../../constants/assets'
import { dashboardRoutes } from '../../constants/routes'
import { useBookings } from '../../hooks/useBookings'
import type { BookingRecord } from '../../types'
import {
  defaultFilters,
  type BookingsFilters,
} from '../../data/bookingFilters'
import { AssignDriverModal } from './components/AssignDriverModal'
import { ViewBookingModal } from './components/ViewBookingModal'
import { VatSelectionModal, type VatRate } from './components/VatSelectionModal'
import { InvoiceModal } from './components/InvoiceModal'
import { NotifyCustomerModal } from './components/NotifyCustomerModal'
// import { SmsModal } from './components/SmsModal'
import { MeetingBoardModal } from './components/MeetingBoardModal'
import { useConfirmDialog } from '../../context/ConfirmDialogContext'

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
}

const MyBookingsPage = () => {
  const navigate = useNavigate()
  const { confirm, showSuccess } = useConfirmDialog()

  const {
    records,
    visible,
    filters,
    isLoading,
    error,
    setFilters,
    resetFilters,
    setTripStatus,
    assignDriver,
    removeDriver,
    removeBooking,
    notifyCustomer,
  } = useBookings()
  
  // Values currently typed/selected in the filter controls.
  // These do NOT automatically filter the booking list.
  const [draftFilters, setDraftFilters] =
    useState<BookingsFilters>({
      ...filters,
    })

  // Main Apply button:
  // applies Search Booking ID, Service Type,
  // Source Name and Date Range preset selections.
  const handleApplyFilters = useCallback(() => {
    setFilters({
      ...draftFilters,
    })
  }, [draftFilters, setFilters])

  // Filters that already have their own Apply button
  // can update the actual table without applying
  // the other pending draft filters.
  const handleImmediateFilterChange = useCallback(
    (changes: Partial<BookingsFilters>) => {
      setFilters((currentFilters) => ({
        ...currentFilters,
        ...changes,
      }))
    },
    [setFilters],
  )

  const handleResetFilters = useCallback(() => {
    resetFilters()

    setDraftFilters({
      ...defaultFilters,
    })
  }, [resetFilters])

  const [assigningBookingId, setAssigningBookingId] = useState<string | null>(null)
  const assigningBooking = records.find(b => b.id === assigningBookingId) || null

  const [viewingBookingId, setViewingBookingId] = useState<string | null>(null)
  const viewingBooking = records.find(b => b.id === viewingBookingId) || null

  const [invoicingBookingId, setInvoicingBookingId] = useState<string | null>(null)
  const invoicingBooking = records.find(b => b.id === invoicingBookingId) || null

  const [vatSelectingBookingId, setVatSelectingBookingId] = useState<string | null>(null)
  const [selectedVatRate, setSelectedVatRate] = useState<VatRate>(10)
  const vatSelectingBooking = records.find(b => b.id === vatSelectingBookingId) || null

  // const [smsBookingId, setSmsBookingId] = useState<string | null>(null)
  // const smsBooking = records.find(b => b.id === smsBookingId) || null

  const [meetingBoardBookingId, setMeetingBoardBookingId] = useState<string | null>(null)
  const meetingBoardBooking =
    records.find(b => b.id === meetingBoardBookingId) || null

  const [notifyBookingId, setNotifyBookingId] = useState<string | null>(null)
  const notifyBooking = records.find(b => b.id === notifyBookingId) || null

  // const hasActiveFilters = Object.values(filters).some(
  //   value => value !== '' && value !== null && value !== undefined,
  // )
  const hasActiveFilters =
    JSON.stringify(filters) !== JSON.stringify(defaultFilters)

    // Default view: today's and future bookings only.
  // When filters are active, preserve the existing filtered results.
  const displayedBookings = (() => {
    if (hasActiveFilters) {
      return visible
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return visible
      .filter((booking) => {
        if (!booking.scheduledAt) return false

        const bookingDate = new Date(booking.scheduledAt)

        return (
          !Number.isNaN(bookingDate.getTime()) &&
          bookingDate >= today
        )
      })
      .sort(
        (a, b) =>
          new Date(a.scheduledAt).getTime() -
          new Date(b.scheduledAt).getTime(),
      )
  })()

  const exportBookingsToExcel = useCallback(() => {
    if (!hasActiveFilters || visible.length === 0) return

    const rows = visible.map((booking) => ({
      'Booking ID': booking.reference,
      'Customer': booking.customer.name,
      'Phone': booking.customerPhone,
      'Email': booking.customer.email,
      'Pickup': booking.route.from,
      'Destination': booking.route.to,
      'Pickup Date/Time': booking.scheduledAt
        ? new Date(booking.scheduledAt).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '',
      'Vehicle': booking.vehicle,
      'Driver': booking.driverName || '',
      'Passengers': booking.passengers,
      'Luggage': booking.luggage,
      'Status': booking.status,
      'Payment': booking.payment,
      'Payment Method': booking.paymentMethod,
      'Deposit (EUR)': Number(booking.depositAmount ?? 0),
      'Estimated Price (EUR)': Number(booking.estimatedPrice ?? 0),
      'Final Price (EUR)': Number(booking.finalPrice ?? 0),
      'Driver Fare (EUR)': Number(booking.driverAmount ?? 0),
      'Notes': booking.notes,
    }))

    const worksheet = XLSX.utils.json_to_sheet(rows)

    worksheet['!cols'] = [
      { wch: 16 },
      { wch: 24 },
      { wch: 18 },
      { wch: 30 },
      { wch: 30 },
      { wch: 30 },
      { wch: 22 },
      { wch: 18 },
      { wch: 24 },
      { wch: 12 },
      { wch: 10 },
      { wch: 16 },
      { wch: 12 },
      { wch: 18 },
      { wch: 16 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 40 },
    ]

    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Bookings',
    )

    const today = new Date().toISOString().split('T')[0]

    XLSX.writeFile(
      workbook,
      `salzburg-bookings-${today}.xlsx`,
    )
  }, [hasActiveFilters, visible])

  const buildRowActions = useCallback(
    (booking: BookingRecord): RowAction[] => [
      {
        id: 'edit',
        label: 'Edit Booking',
        icon: Edit,
        onSelect: () =>
          navigate(
            dashboardRoutes.bookingEdit.replace(':id', booking.id),
          ),
      },
      {
        id: 'view',
        label: 'View',
        icon: Eye,
        onSelect: () => setViewingBookingId(booking.id),
      },
      {
        id: 'invoice',
        label: 'Download Invoice',
        icon: FileText,
        onSelect: () => setVatSelectingBookingId(booking.id),
      },
      // {
      //   id: 'sms',
      //   label: 'SMS',
      //   icon: MessageSquare,
      //   onSelect: () => setSmsBookingId(booking.id),
      // },
      {
        id: 'meetingBoard',
        label: 'Meeting Board',
        icon: IdCard,
        onSelect: () => setMeetingBoardBookingId(booking.id),
      },
      {
        id: 'notify',
        label: 'Notify',
        icon: Mail,
        onSelect: () => setNotifyBookingId(booking.id),
      },
      {
        id: 'cancel',
        label: 'Remove Booking',
        icon: XCircle,
        danger: true,
        onSelect: async () => {
          const confirmed = await confirm({
            title: 'Remove Booking',
            message: `Are you sure you want to remove booking #${booking.reference}?`,
            confirmText: 'Remove Booking',
            cancelText: 'Keep Booking',
            variant: 'danger',
            icon: 'x-circle',
          })

          if (confirmed) {
            removeBooking(booking.id)

            await showSuccess({
              title: 'Booking Removed',
              message: `Booking #${booking.reference} has been removed successfully.`,
            })
          }
        },
      },
      ...(booking.driverAssigned
        ? [
            {
              id: 'reassign',
              label: 'Reassign Driver',
              icon: UserCog,
              onSelect: () => setAssigningBookingId(booking.id),
            },
            {
              id: 'removeDriver',
              label: 'Remove Driver',
              icon: UserMinus,
              danger: true,
              onSelect: async () => {
                const confirmed = await confirm({
                  title: 'Remove Driver',
                  message: `Are you sure you want to unassign ${(booking as any).driverName || 'the driver'} from booking #${booking.reference}?`,
                  confirmText: 'Remove Driver',
                  cancelText: 'Cancel',
                  variant: 'warning',
                  icon: 'user-minus',
                })

                if (confirmed) {
                  console.log('REMOVE DRIVER ACTION:', booking.id)

                  await removeDriver(booking.id)

                  await showSuccess({
                    title: 'Driver Removed',
                    message: 'Driver has been unassigned from the booking.',
                  })
                }
              },
            },
          ]
        : [
            {
              id: 'assignDriver',
              label: 'Assign Driver',
              icon: UserPlus,
              onSelect: () => setAssigningBookingId(booking.id),
            },
          ]),
    ],
    [removeBooking, removeDriver, confirm, showSuccess, navigate],
  )

  return (
    <DashboardLayout
      activeNavId="my-bookings"
      contentGap={15}
      subtitle={
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: dashboardRoutes.overview },
            { label: 'Bookings' },
          ]}
        />
      }
      title="Bookings"
      user={currentUser}
    >
      <div className="flex min-h-0 flex-col gap-[19px] lg:max-h-[calc(100dvh-175px)] lg:overflow-y-auto">
        <div className="shrink-0">
          <BookingsFilterBar
            filters={draftFilters}
            onChange={setDraftFilters}
            onApply={handleApplyFilters}
            onImmediateChange={handleImmediateFilterChange}
            onNewBooking={() => navigate(dashboardRoutes.bookingNew)}
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E0E0E0] border-t-[#FF6A1A]" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center rounded-lg bg-red-50 p-4 text-center font-host text-red-600">
            {error}
          </div>
        ) : (
          <BookingsTableCard
            // bookings={visible}
            bookings={displayedBookings}
            buildRowActions={buildRowActions}
            onAssignDriver={setAssigningBookingId}
            onToggleFilters={handleResetFilters}
            onTripStatusChange={setTripStatus}
            // totalCount={visible.length}
            // onExportExcel={exportBookingsToExcel}
            totalCount={displayedBookings.length}
            onExportExcel={exportBookingsToExcel}
            exportDisabled={!hasActiveFilters || visible.length === 0}
          />
        )}
      </div>

      <AssignDriverModal
        isOpen={!!assigningBookingId}
        onClose={() => setAssigningBookingId(null)}
        booking={assigningBooking}
        onAssign={async (driverIds, amounts, meetingBoardUrl, driverName, includeDriverPhone) => {
          if (!assigningBookingId || !driverIds[0]) {
            return false
          }

          const driverId = driverIds[0]

          const driverFare =
            amounts?.[driverId] ||
            String(assigningBooking?.amountEur || 0)

          return await assignDriver(
            assigningBookingId,
            driverId,
            driverFare,
            meetingBoardUrl,
            driverName,
            includeDriverPhone
          )
        }}
      />

      <ViewBookingModal
        isOpen={!!viewingBookingId}
        onClose={() => setViewingBookingId(null)}
        booking={viewingBooking}
      />

      <VatSelectionModal
        isOpen={!!vatSelectingBookingId}
        onClose={() => setVatSelectingBookingId(null)}
        booking={vatSelectingBooking}
        initialVat={selectedVatRate}
        onConfirm={(vat) => {
          setSelectedVatRate(vat)
          setInvoicingBookingId(vatSelectingBookingId)
          setVatSelectingBookingId(null)
        }}
      />

      <InvoiceModal
        isOpen={!!invoicingBookingId}
        onClose={() => setInvoicingBookingId(null)}
        booking={invoicingBooking}
        vatRate={selectedVatRate}
        onVatRateChange={setSelectedVatRate}
      />

      <NotifyCustomerModal
        isOpen={!!notifyBookingId}
        onClose={() => setNotifyBookingId(null)}
        booking={notifyBooking}
        onSend={(subject, message) =>
          notifyBookingId
            ? notifyCustomer(notifyBookingId, subject, message)
            : Promise.resolve(false)
        }
      />

      {/*
      <SmsModal
        isOpen={!!smsBookingId}
        onClose={() => setSmsBookingId(null)}
        booking={smsBooking}
      /> */}

      <MeetingBoardModal
        isOpen={!!meetingBoardBookingId}
        onClose={() => setMeetingBoardBookingId(null)}
        booking={meetingBoardBooking}
      />
    </DashboardLayout>
  )
}

export default MyBookingsPage